import { useEffect, useRef } from 'react';
import { recurringPaymentsApi } from '../../services/recurringPaymentsApi';
import { paymentCloseMessage } from '../../lib/userErrors';

const POLL_MS = 8000;

export type PaymentReviewOutcome = 'pending' | 'active' | { declined: string };

export async function reviewPendingPayment(): Promise<PaymentReviewOutcome> {
    const data = await recurringPaymentsApi.getSubscription({ force: true });
    if (data.subscription?.status === 'pending_first_payment') return 'pending';
    if (data.subscription?.status === 'active') return 'active';
    return { declined: paymentCloseMessage(data.payment_result?.code) };
}

/** While the first charge is in review, ask the server every few seconds. */
export function usePaymentReviewPoll(
    enabled: boolean,
    onApproved: () => void,
    onDeclined: (message: string) => void,
) {
    const onApprovedRef = useRef(onApproved);
    const onDeclinedRef = useRef(onDeclined);
    onApprovedRef.current = onApproved;
    onDeclinedRef.current = onDeclined;

    useEffect(() => {
        if (!enabled) return;
        let stopped = false;

        const check = async () => {
            if (stopped) return;
            try {
                const outcome = await reviewPendingPayment();
                if (stopped || outcome === 'pending') return;
                stopped = true;
                if (outcome === 'active') onApprovedRef.current();
                else onDeclinedRef.current(outcome.declined);
            } catch (err) {
                console.error(err);
            }
        };

        const id = window.setInterval(check, POLL_MS);
        return () => {
            stopped = true;
            window.clearInterval(id);
        };
    }, [enabled]);
}
